import { useEffect, useState, forwardRef, useMemo } from "react";
import { ActionSuggestion, CheckInData, UserPersona } from "@/types/user";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, Zap, Clock, Users, BookOpen, Star, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import BreakTimerModal from "./modals/BreakTimerModal";
import ReflectionModal from "./modals/ReflectionModal";
import GoalSettingModal from "./modals/GoalSettingModal";
import FocusTimeModal from "./modals/FocusTimeModal";
import TeamCheckinModal from "./modals/TeamCheckinModal";
import CelebrateModal from "./modals/CelebrateModal";
import { useUser } from "@/contexts/UserContext";
import { useNavigate } from "react-router-dom";
import { ActionModal, ActionableInsight } from "./insights/ActionModal";
import { insightsService } from "@/services/insightsService";
import { teamsService } from "@/services/teamsService";
import { notificationsService } from "@/services/notificationsService";

type ActionSuggestionsProps = {
  onActionSelected: (action: ActionSuggestion) => void;
  checkInData?: CheckInData | {
    energy: number;
    motivation: number;
    mood: number;
  };
  userPersona?: UserPersona;
  recentHistory?: (CheckInData | any)[];
  preGeneratedSuggestions?: ActionSuggestion[]; // Allow passing pre-generated suggestions
  isDashboard?: boolean; // Distinguish between dashboard and check-in context
};

// Helper function to log the action for analytics
const logAction = ({
  userId,
  actionType,
  timestamp = new Date(),
}: {
  userId: string;
  actionType: string;
  timestamp?: Date;
}) => {
  console.log("Action logged:", { userId, actionType, timestamp });
  // In a real app, this would send the data to an analytics API
};

// Function to generate random action suggestions (fallback)
const generateRandomSuggestions = (
  userId: string,
  checkInData?: { energy: number; motivation: number; mood: number }
): ActionSuggestion[] => {
  const actionTypes = [
    "break",
    "connect",
    "focus",
    "reflect",
    "celebrate",
  ] as const;
  const priorities = ["high", "medium", "low"] as const;

  // Use userId and current date to seed the random generation
  const today = new Date().toISOString().split("T")[0];
  const seed = `${userId}-${today}`;

  // Simple hash function to generate consistent random numbers
  const hash = seed.split("").reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);

  const suggestions: ActionSuggestion[] = [];

  // If no check-in data, generate completely random suggestions
  if (!checkInData) {
    for (let i = 0; i < 3; i++) {
      const actionTypeIndex = Math.abs(hash + i) % actionTypes.length;
      const priorityIndex = Math.abs(hash + i + 1) % priorities.length;

      const actionType = actionTypes[actionTypeIndex];
      const priority = priorities[priorityIndex];

      let title = "";
      let description = "";
      let customActionType: ActionSuggestion["customActionType"];

      switch (actionType) {
        case "break":
          title = "Take a Short Break";
          description =
            "A quick break can help refresh your mind and boost productivity.";
          customActionType = "break_timer";
          break;
        case "connect":
          title = "Team Check-in";
          description =
            "Connect with your team members to share updates and progress.";
          customActionType = "team_checkin";
          break;
        case "focus":
          title = "Focus Time";
          description =
            "Schedule a focused work session to tackle important tasks.";
          customActionType = "schedule_focus";
          break;
        case "reflect":
          title = "Daily Reflection";
          description =
            "Take a moment to reflect on your achievements and challenges.";
          customActionType = "log_reflection";
          break;
        case "celebrate":
          title = "Celebrate Progress";
          description =
            "Acknowledge and celebrate your recent accomplishments.";
          customActionType = "celebrate_progress";
          break;
      }

      suggestions.push({
        id: `suggestion-${userId}-${today}-${i}`,
        actionType,
        title,
        description,
        priority,
        customActionType,
      });
    }
    return suggestions;
  }

  // Rule-based suggestions based on mood/energy
  const normalizedEnergy = checkInData.energy / 100;
  const normalizedMotivation = checkInData.motivation / 100;
  const normalizedMood = (checkInData.mood - 1) / 4;

  const energyScore = normalizedEnergy;
  const motivationScore = normalizedMotivation;
  const moodScore = normalizedMood;
  const overallScore = (energyScore + motivationScore + moodScore) / 3;

  //Définition des Poids des ActionS
  const actionWeights: Record<ActionSuggestion["actionType"], number> = {
    break: energyScore < 0.4 ? 0.4 : energyScore < 0.6 ? 0.2 : 0.1, 
    connect: energyScore < 0.3 || motivationScore < 0.3 ? 0.3 : 0.2,
    focus: energyScore > 0.6 && motivationScore > 0.6 ? 0.4 : 0.2,
    reflect: energyScore > 0.7 && motivationScore > 0.7 ? 0.3 : 0.2, 
    celebrate: energyScore > 0.8 && motivationScore > 0.8 ? 0.3 : 0.1, 
  };

  //Ajustement Dynamique des Poids
  if (overallScore < 0.3) {
    actionWeights.break *= 1.5;
    actionWeights.connect *= 1.3;
  } else if (overallScore > 0.7) {
    actionWeights.focus *= 1.3;
    actionWeights.reflect *= 1.2;
    actionWeights.celebrate *= 1.2;
  }

  // Génération des Suggestions de facçon aléatoire
  for (let i = 0; i < 3; i++) {
    const cumulativeWeights = Object.entries(actionWeights).reduce(
      (acc, [type, weight], index) => {
        acc[type] = (acc[Object.keys(acc)[index - 1]] || 0) + weight;
        return acc;
      },
      {} as Record<ActionSuggestion["actionType"], number>
    );

    const totalWeight = Object.values(actionWeights).reduce((a, b) => a + b, 0);
    const random = Math.random() * totalWeight;

    let selectedType = actionTypes[0] as ActionSuggestion["actionType"];
    for (const [type, weight] of Object.entries(cumulativeWeights)) {
      if (random <= weight) {
        selectedType = type as ActionSuggestion["actionType"];
        break;
      }
    }

    // Définition de la Priorité
    let priority: ActionSuggestion["priority"];
    if (energyScore < 0.3 || motivationScore < 0.3) {
      priority = "high";
    } else if (energyScore < 0.6 || motivationScore < 0.6) {
      priority = "medium";
    } else {
      priority = "low";
    }

    // Définition du Titre et de la Description de la Suggestion
    let title = "";
    let description = "";
    let customActionType: ActionSuggestion["customActionType"];

    switch (selectedType) {
      case "break":
        title = "Take a Short Break";
        description =
          energyScore < 0.4
            ? "Your energy levels are low. A quick break can help you recharge and boost productivity."
            : "A short break can help maintain your energy levels and improve focus.";
        customActionType = "break_timer";
        break;
      case "connect":
        title = "Team Check-in";
        description =
          energyScore < 0.3 || motivationScore < 0.3
            ? "Connect with your team members to boost your energy and motivation."
            : "Share your positive energy with the team and strengthen collaboration.";
        customActionType = "team_checkin";
        break;
      case "focus":
        title = "Focus Time";
        description =
          energyScore > 0.6 && motivationScore > 0.6
            ? "Your high energy and motivation make this a perfect time for focused work."
            : "Schedule a focused work session to build momentum and increase motivation.";
        customActionType = "schedule_focus";
        break;
      case "reflect":
        title = "Daily Reflection";
        description =
          energyScore > 0.7 && motivationScore > 0.7
            ? "Your high energy and motivation levels make this a great time to reflect on your achievements."
            : "Take a moment to reflect on your progress and set intentions for tomorrow.";
        customActionType = "log_reflection";
        break;
      case "celebrate":
        title = "Celebrate Progress";
        description =
          energyScore > 0.8 && motivationScore > 0.8
            ? "Your exceptional energy and motivation deserve celebration! Share your success with the team."
            : "Acknowledge your progress and celebrate your accomplishments.";
        customActionType = "celebrate_progress";
        break;
    }

    suggestions.push({
      id: `suggestion-${userId}-${today}-${i}`,
      actionType: selectedType,
      title,
      description,
      priority,
      customActionType,
    });
  }

  return suggestions;
};

const ActionSuggestions = forwardRef<HTMLDivElement, ActionSuggestionsProps>(
  ({ onActionSelected, checkInData, userPersona, recentHistory, preGeneratedSuggestions, isDashboard = false }, ref) => {
    const { toast } = useToast();
    const { user } = useUser();
    const navigate = useNavigate();
    
    // State declarations - MUST be called before any conditional returns
    const [suggestions, setSuggestions] = useState<ActionSuggestion[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [lastGeneratedKey, setLastGeneratedKey] = useState<string>('');
    const [actionModalOpen, setActionModalOpen] = useState(false);
    const [selectedInsight, setSelectedInsight] = useState<ActionableInsight | null>(null);
    const [aiInsights, setAiInsights] = useState<ActionableInsight[]>([]);
    const [aiInsightsRefreshKey, setAiInsightsRefreshKey] = useState(0);
    const [isForceRefresh, setIsForceRefresh] = useState(false);
    
    // State for different modals - MUST be called before any conditional returns
    const [breakTimerOpen, setBreakTimerOpen] = useState(false);
    const [reflectionOpen, setReflectionOpen] = useState(false);
    const [goalSettingOpen, setGoalSettingOpen] = useState(false);
    const [focusTimeOpen, setFocusTimeOpen] = useState(false);
    const [teamCheckinOpen, setTeamCheckinOpen] = useState(false);
    const [celebrationOpen, setCelebrationOpen] = useState(false);
    const [currentAction, setCurrentAction] = useState<ActionSuggestion | null>(
      null
    );

    // Memoize stable checkInData to prevent unnecessary re-renders - MUST be called before any conditional returns
    const stableCheckInData = useMemo(() => {
      if (!checkInData) return null;
      return {
        mood: checkInData.mood || 3,
        energy: checkInData.energy || 50,
        focus: (checkInData as any).focus || 50,
        motivation: (checkInData as any).motivation || 50,
        ...('tasks' in checkInData ? checkInData : {})
      };
    }, [checkInData?.mood, checkInData?.energy, (checkInData as any)?.focus, (checkInData as any)?.motivation, (checkInData as any)?.tasks?.length]);

    // Generate cache key to prevent unnecessary re-generations - MUST be called before any conditional returns
    const cacheKey = useMemo(() => {
      const key = [
        user?.id || '',
        isDashboard ? 'dashboard' : 'checkin',
        stableCheckInData ? JSON.stringify(stableCheckInData) : '',
        userPersona || user?.persona || '',
        preGeneratedSuggestions?.length || 0,
        '',
        // For dashboard, include a daily cache key so it refreshes daily
        isDashboard ? new Date().toDateString() : ''
      ].join('|');
      return key;
    }, [user?.id, isDashboard, stableCheckInData, userPersona, user?.persona, preGeneratedSuggestions?.length]);

    // Load AI insights for dashboard view - MUST be called before any conditional returns
    // MIGRATED TO BACKEND: Previously used client-side openaiService.generateDashboardSuggestions() and AIInsightsService
    // Now uses backend API: GET /insights/actionable-insights (primary) and GET /insights/personalized (fallback)
    useEffect(() => {
      const loadAIInsights = async () => {
        if (isDashboard && user) {
          try {
            console.log('🔄 Loading AI insights from backend - Force refresh:', isForceRefresh);
            
            // MIGRATED TO BACKEND: Previously AIInsightsService.generateActionableInsights() (client-side)
            // Backend endpoint: GET /insights/actionable-insights
            // Returns: ActionableInsight[] (hardcoded pattern detection logic)
            const actionableInsights = await insightsService.getActionableInsights();
            
            console.log('🤖 Backend actionable insights received:', actionableInsights?.length, 'suggestions');
            
            // Reset force refresh flag after use
            if (isForceRefresh) {
              setIsForceRefresh(false);
            }
            
            // Use actionable insights directly (already in ActionableInsight format)
            const formattedInsights: ActionableInsight[] = Array.isArray(actionableInsights) ? actionableInsights : [];
            
            console.log('✅ Final actionable insights:', formattedInsights.map(i => i.title));
            setAiInsights(formattedInsights);
          } catch (error) {
            console.error('❌ Error loading actionable insights:', error);
            console.log('🔄 Falling back to personalized insights (GPT-4o)');
            // MIGRATED TO BACKEND: Previously used client-side openaiService.generateDashboardSuggestions()
            // Backend endpoint: GET /insights/personalized (dashboardInsights field uses GPT-4o)
            // Fallback to personalized insights (GPT-4o powered) if actionable insights fail
            try {
              const response = await insightsService.getPersonalizedInsights();
              const dashboardInsights = response?.dashboardInsights || [];
              console.log('📋 Fallback insights loaded:', dashboardInsights?.length, 'suggestions');
              // dashboardInsights is already ActionableInsight[] format (no transformation needed)
              const formattedFallbackInsights: ActionableInsight[] = Array.isArray(dashboardInsights) ? dashboardInsights : [];
              setAiInsights(formattedFallbackInsights);
            } catch (fallbackError) {
              console.error('❌ Error loading fallback insights:', fallbackError);
            }
          }
        }
      };
      loadAIInsights();
    }, [isDashboard, user?.id, aiInsightsRefreshKey, isForceRefresh]);

    // Generate AI-powered suggestions - MUST be called before any conditional returns
    // MIGRATED TO BACKEND: Dashboard suggestions logic
    // Previously (web_old): openaiService.generateDashboardSuggestions() - client-side GPT-4o
    // Now: GET /insights/personalized (dashboardInsights field) - server-side GPT-4o
    // Check-in suggestions: POST /insights/suggestions - server-side processing
    useEffect(() => {
      const generateSuggestions = async () => {
        if (!user) {
          setIsLoading(false);
          return;
        }

        // If pre-generated suggestions are provided, use them instead
        if (preGeneratedSuggestions && preGeneratedSuggestions.length > 0) {
          setSuggestions(preGeneratedSuggestions.slice(0, 3));
          setIsLoading(false);
          setLastGeneratedKey(cacheKey);
          return;
        }

        // Skip if we already generated suggestions for this exact same data
        if (lastGeneratedKey === cacheKey && suggestions.length > 0) {
          console.log('🎯 Skipping AI generation - using existing suggestions for same data');
          setIsLoading(false);
          return;
        }

        console.log(`🤖 Generating ${isDashboard ? 'dashboard' : 'check-in'} suggestions for cache key:`, cacheKey.substring(0, 50) + '...');

        setIsLoading(true);
        try {
          let aiSuggestions: ActionSuggestion[] = [];

          if (isDashboard) {
            // Dashboard suggestions: Try GPT-4o first, then fallback to hardcoded (matching web_old)
            try {
              // MIGRATED TO BACKEND: Previously openaiService.generateDashboardSuggestions(userPersona, userHistory, recentReflections)
              // Backend endpoint: GET /insights/personalized (uses GPT-4o via dashboardInsights.service.ts)
              // Try GPT-4o powered insights first (matching web_old behavior)
              const response = await insightsService.getPersonalizedInsights();
              const dashboardInsights = response?.dashboardInsights || [];
              
              // Convert ActionableInsight[] to ActionSuggestion[] (backend already returns ActionableInsight format)
              aiSuggestions = dashboardInsights.map((insight: ActionableInsight) => ({
                id: insight.id,
                title: insight.title,
                description: insight.description,
                actionType: insight.actionType as ActionSuggestion['actionType'],
                priority: insight.impact as ActionSuggestion['priority'],
                customActionType: insight.actionType,
              }));
            } catch (gptError) {
              console.error('❌ Error loading GPT-4o suggestions, falling back to hardcoded:', gptError);
              try {
                // MIGRATED TO BACKEND: Previously AIInsightsService.generateActionableInsights() (client-side fallback)
                // Backend endpoint: GET /insights/actionable-insights
                // Fallback to hardcoded pattern detection (matching web_old fallback logic)
                const actionableInsights = await insightsService.getActionableInsights();
                aiSuggestions = actionableInsights.map((insight: ActionableInsight) => ({
                  id: insight.id,
                  title: insight.title,
                  description: insight.description,
                  actionType: insight.actionType as ActionSuggestion['actionType'],
                  priority: insight.impact as ActionSuggestion['priority'],
                  customActionType: insight.actionType,
                }));
              } catch (hardcodedError) {
                console.error('❌ Error loading hardcoded suggestions:', hardcodedError);
                // Will fallback to generateRandomSuggestions() below
              }
            }
          } else if (stableCheckInData) {
            // MIGRATED TO BACKEND: Check-in suggestions logic
            // Previously (web_old): openaiService.generateActionSuggestions(formattedCheckInData, userPersona, recentHistory) - client-side
            // Now: POST /insights/suggestions - server-side processing
            // Check-in suggestions based on today's specific data
            const formattedCheckInData: CheckInData = {
              id: 'temp-' + Date.now(),
              userId: user.id,
              date: new Date().toISOString(),
              ...stableCheckInData,
              tasks: [],
              ...('tasks' in stableCheckInData ? stableCheckInData : {})
            };

            // MIGRATED TO BACKEND: Previously openaiService.generateActionSuggestions() with formattedCheckInData
            // Backend endpoint: POST /insights/suggestions
            // Generate suggestions via backend API
            aiSuggestions = await insightsService.generateSuggestions({
              currentCheckIn: formattedCheckInData,
              limit: 5,
            });
          }

          // Fallback to rule-based suggestions if AI doesn't return enough
          if (aiSuggestions.length === 0) {
            aiSuggestions = generateRandomSuggestions(user.id, checkInData ? {
              energy: checkInData.energy || 50,
              motivation: (checkInData as any).motivation || 50,
              mood: checkInData.mood || 3,
            } : undefined);
          }

          setSuggestions(aiSuggestions.slice(0, 3)); // Limit to 3 suggestions
          setLastGeneratedKey(cacheKey); // Mark this data as processed
        } catch (error) {
          console.error('Error generating AI suggestions:', error);
          // Fallback to random suggestions
          setSuggestions(generateRandomSuggestions(user.id, stableCheckInData));
          setLastGeneratedKey(cacheKey); // Even for fallbacks, mark as processed
        } finally {
          setIsLoading(false);
        }
      };

      // Debounce the generation to prevent rapid-fire API calls
      const timeoutId = setTimeout(() => {
        generateSuggestions();
      }, 300); // 300ms debounce

      return () => clearTimeout(timeoutId);
    }, [user?.id, cacheKey, lastGeneratedKey, suggestions.length]); // Include stable dependencies

    // Memoize display suggestions to avoid nested map warning - MUST be called before any conditional returns
    const displaySuggestions = useMemo(() => {
      return isDashboard && aiInsights.length > 0 ? 
        aiInsights.map(insight => ({
          id: insight.id,
          title: insight.title,
          description: insight.description,
          actionType: insight.actionType as ActionSuggestion['actionType'],
          priority: insight.impact as ActionSuggestion['priority']
        })) : 
        suggestions;
    }, [isDashboard, aiInsights, suggestions]);

    // Add error boundary protection - AFTER all hooks are called
    if (!user) {
      return (
        <div className="space-y-4">
          <div className="text-center p-6">
            <p className="text-muted-foreground">Loading user data...</p>
          </div>
        </div>
      );
    }

    // Helper function to get icon based on action type
    const getActionIcon = (type: ActionSuggestion["actionType"]) => {
      switch (type) {
        case "break":
          return <Clock className="h-5 w-5 text-blue-500" />;
        case "connect":
          return <Users className="h-5 w-5 text-purple-500" />;
        case "focus":
          return <Zap className="h-5 w-5 text-amber-500" />;
        case "reflect":
          return <BookOpen className="h-5 w-5 text-emerald-500" />;
        case "celebrate":
          return <Star className="h-5 w-5 text-pink-500" />;
        default:
          return <Zap className="h-5 w-5 text-primary" />;
      }
    };

    const handleActionClick = async (action: ActionSuggestion, index?: number) => {
      setCurrentAction(action);

      // Log for analytics
      if (user) {
        logAction({
          userId: user.id,
          actionType: action.actionType,
        });
      }

      // If this is from AI insights (dashboard mode), open the ActionModal
      if (isDashboard && aiInsights.length > 0 && index !== undefined && index < aiInsights.length) {
        const insight = aiInsights[index];
        setSelectedInsight(insight);
        setActionModalOpen(true);
        return;
      }

      // Otherwise, use the existing modal logic
      switch (action.customActionType) {
        case "schedule_focus":
          setFocusTimeOpen(true);
          break;
        case "log_reflection":
          setReflectionOpen(true);
          break;
        case "set_goal":
          setGoalSettingOpen(true);
          break;
        case "break_timer":
          setBreakTimerOpen(true);
          break;
        default:
          // For other action types based on actionType
          switch (action.actionType) {
            case "connect":
              setTeamCheckinOpen(true);
              break;
            case "celebrate":
              setCelebrationOpen(true);
              break;
            case "reflect":
              console.log("Redirecting to profile with goals tab");
              navigate("/profile", {
                state: { activeTab: "goals" },
                replace: true,
              });
              break;
            default:
              // For other action types, just call the callback
              onActionSelected(action);

              // Show toast for user feedback
              toast({
                title: "Action selected",
                description: `You've selected: ${action.title}`,
              });
          }
      }
    };

    // Handle focus time scheduling
    const handleScheduleFocus = (data: any) => {
      toast({
        title: "Focus Time scheduled",
        description: `${
          data.title
        } scheduled for ${data.startTime.toLocaleString()}`,
      });

      if (currentAction) {
        onActionSelected(currentAction);
      }
    };

    // Handle team check-in
    // MIGRATED TO BACKEND: Previously used Supabase client queries
    // Now: teamsService.getMembers() → GET /teams/{teamId}/members
    //      notificationsService.create() → POST /notifications
    // ALIGNED WITH web_old: Check only if user exists (not user.teamId)
    const handleTeamCheckin = async (data: any) => {
      if (user) {
        try {
          // MIGRATED TO BACKEND: Get all team members via teamsService
          // Backend endpoint: GET /teams/{teamId}/members
          // apiService.get() automatically extracts response.data.data, so this returns TeamMember[] directly
          const teamMembers = await teamsService.getMembers(user.teamId);
          const membersArray = Array.isArray(teamMembers) ? teamMembers : [];
          
          console.log("teamMembers", membersArray);
          // Send notification to each team member
          for (const member of membersArray) {
            if (member.id !== user.id) {
              console.log("Sending notification to", member.id);
              // MIGRATED TO BACKEND: Previously createNotification(supabase, member.user_id, ...)
              // Backend endpoint: POST /notifications
              // Backend expects userId in the body to specify the recipient
              // Note: Type definition doesn't include userId, but backend requires it
              await notificationsService.create({
                userId: member.id,
                type: 'team_alert',
                title: "Team Check-in Request",
                message: `${user.name} wants to connect with the team for a check-in.`,
                priority: 'normal',
                data: { type: "team_checkin", link: "/checkin" }
              } as any);
            }
          }

          toast({
            title: "Check-in sent",
            description: `Message sent to ${
              data.recipients.length
            } team member${data.recipients.length !== 1 ? "s" : ""}`,
          });

          if (currentAction) {
            onActionSelected(currentAction);
          }
        } catch (error) {
          console.error("Error sending team notifications:", error);
          toast({
            title: "Error",
            description: "Failed to send notifications to team members",
            variant: "destructive",
          });
        }
      }
    };

    // Handle reflection submission
    const handleReflectionSubmit = (data: any) => {
      toast({
        title: "Reflection saved",
        description: "Your reflection has been saved successfully.",
      });

      if (currentAction) {
        onActionSelected(currentAction);
      }
    };

    // Handle celebration
    const handleCelebration = (data: any) => {
      const shareText = data.shareWithTeam ? "and shared with your team" : "";
      toast({
        title: "Progress celebrated",
        description: `${data.emoji} Your celebration has been saved ${shareText}!`,
      });

      if (currentAction) {
        onActionSelected(currentAction);
      }
    };

    return (
      <div ref={ref} className="space-y-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full animate-pulse"></div>
            <h2 className="text-2xl font-bold tracking-tight">
              ✨ {isLoading 
                ? (isDashboard ? "AI Analyzing Your Patterns..." : "AI Analyzing Your Check-in...") 
                : (isDashboard ? "AI Pattern Insights" : "AI-Powered Suggestions")
              }
            </h2>
          </div>
          {isDashboard && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                // Force refresh by clearing cache and setting new cache key
                setLastGeneratedKey('');
                setIsForceRefresh(true);
                setAiInsightsRefreshKey(prev => prev + 1);
                console.log('🔄 Refresh clicked - forcing fresh AI insights generation');
                
                // Generate new suggestions from backend immediately (matching web_old refresh logic)
                const generateFreshSuggestions = async () => {
                  setIsLoading(true);
                  try {
                    let aiSuggestions: ActionSuggestion[] = [];
                    
                    // MIGRATED TO BACKEND: Refresh button logic - same as generateSuggestions() above
                    // Previously (web_old): openaiService.generateDashboardSuggestions() - client-side
                    // Now: GET /insights/personalized - server-side
                    // Try GPT-4o first, then fallback to hardcoded (matching web_old)
                    try {
                      // MIGRATED TO BACKEND: GET /insights/personalized (GPT-4o powered)
                      const response = await insightsService.getPersonalizedInsights();
                      const dashboardInsights = response?.dashboardInsights || [];
                      
                      // Convert ActionableInsight[] to ActionSuggestion[]
                      aiSuggestions = dashboardInsights.map((insight: ActionableInsight) => ({
                        id: insight.id,
                        title: insight.title,
                        description: insight.description,
                        actionType: insight.actionType as ActionSuggestion['actionType'],
                        priority: insight.impact as ActionSuggestion['priority'],
                        customActionType: insight.actionType,
                      }));
                    } catch (gptError) {
                      console.error('❌ Error loading GPT-4o suggestions, falling back to hardcoded:', gptError);
                      try {
                        // MIGRATED TO BACKEND: GET /insights/actionable-insights (hardcoded fallback)
                        const actionableInsights = await insightsService.getActionableInsights();
                        aiSuggestions = actionableInsights.map((insight: ActionableInsight) => ({
                          id: insight.id,
                          title: insight.title,
                          description: insight.description,
                          actionType: insight.actionType as ActionSuggestion['actionType'],
                          priority: insight.impact as ActionSuggestion['priority'],
                          customActionType: insight.actionType,
                        }));
                      } catch (hardcodedError) {
                        console.error('❌ Error loading hardcoded suggestions:', hardcodedError);
                      }
                    }
                    
                    // Fallback to random if still empty
                    if (aiSuggestions.length === 0) {
                      aiSuggestions = generateRandomSuggestions(user.id, checkInData ? {
                        energy: checkInData.energy || 50,
                        motivation: (checkInData as any).motivation || 50,
                        mood: checkInData.mood || 3,
                      } : undefined);
                    }
                    
                    setSuggestions(aiSuggestions.slice(0, 3));
                    setLastGeneratedKey('');
                    setIsForceRefresh(false);
                  } catch (error) {
                    console.error('Error generating fresh suggestions:', error);
                    // Final fallback to random suggestions
                    setSuggestions(generateRandomSuggestions(user.id, checkInData ? {
                      energy: checkInData.energy || 50,
                      motivation: (checkInData as any).motivation || 50,
                      mood: checkInData.mood || 3,
                    } : undefined).slice(0, 3));
                  } finally {
                    setIsLoading(false);
                  }
                };
                
                generateFreshSuggestions();
              }}
              disabled={isLoading}
              className="h-8 w-8 p-0"
            >
              <RefreshCw className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          )}
        </div>
        <p className="text-muted-foreground">
          {isLoading 
            ? (isDashboard 
              ? "Analyzing your historical patterns and trends to suggest optimizations..."
              : "Analyzing your check-in data to generate personalized suggestions..."
            )
            : (isDashboard
              ? "Based on your patterns and trends, here are insights to optimize your wellbeing and productivity."
              : "Based on today's check-in, here are personalized recommendations for right now."
            )
          }
        </p>

        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mt-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="overflow-hidden">
                <CardHeader className="pb-2">
                  <div className="animate-pulse">
                    <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-muted rounded w-full mb-1"></div>
                    <div className="h-3 bg-muted rounded w-2/3"></div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="animate-pulse h-8 bg-muted rounded"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mt-4">
            {displaySuggestions.map((suggestion) => (
              <Card
                key={suggestion.id}
                className="overflow-hidden transition-all hover:shadow-md"
              >
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div className="p-2 bg-primary/10 rounded-md">
                      {getActionIcon(suggestion.actionType)}
                    </div>
                    {suggestion.priority && (
                      <div
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          suggestion.priority === "high"
                            ? "bg-red-100 text-red-700"
                            : suggestion.priority === "medium"
                            ? "bg-amber-100 text-amber-700"
                            : "bg-green-100 text-green-700"
                        }`}
                      >
                        {suggestion.priority.charAt(0).toUpperCase() +
                          suggestion.priority.slice(1)}
                      </div>
                    )}
                  </div>
                  <CardTitle className="text-lg mt-2">
                    {suggestion.title}
                  </CardTitle>
                  <CardDescription>{suggestion.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={() => {
                      const index = displaySuggestions.findIndex(s => s.id === suggestion.id);
                      handleActionClick(suggestion, index);
                    }}
                    variant="ghost"
                    className="flex items-center justify-between w-full"
                  >
                    <span>Take action</span>
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Modals for different action types */}
        <BreakTimerModal
          isOpen={breakTimerOpen}
          onOpenChange={setBreakTimerOpen}
        />

        <FocusTimeModal
          isOpen={focusTimeOpen}
          onOpenChange={setFocusTimeOpen}
          onSchedule={handleScheduleFocus}
        />

        <TeamCheckinModal
          isOpen={teamCheckinOpen}
          onOpenChange={setTeamCheckinOpen}
          onSend={handleTeamCheckin}
        />

        <ReflectionModal
          isOpen={reflectionOpen}
          onOpenChange={setReflectionOpen}
          onSubmit={handleReflectionSubmit}
        />

        <GoalSettingModal
          isOpen={goalSettingOpen}
          onOpenChange={setGoalSettingOpen}
          onSubmit={(data) => {
            if (currentAction) {
              onActionSelected(currentAction);
            }
            toast({
              title: "Goal set",
              description: "Your goal has been set successfully.",
            });
          }}
        />

        <CelebrateModal
          isOpen={celebrationOpen}
          onOpenChange={setCelebrationOpen}
          onSubmit={handleCelebration}
        />

        {/* AI-powered Action Modal */}
        <ActionModal
          isOpen={actionModalOpen}
          onClose={() => {
            setActionModalOpen(false);
            setSelectedInsight(null);
          }}
          insight={selectedInsight}
          onActionComplete={() => {
            toast({
              title: "Action executed successfully",
              description: "Your AI-recommended action has been set up.",
            });
            if (currentAction) {
              onActionSelected(currentAction);
            }
          }}
        />
      </div>
    );
  }
);

ActionSuggestions.displayName = "ActionSuggestions";

export default ActionSuggestions;